import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@ohif/ui';
import { customStyles, maskEmail } from '../../Utils/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { otpVerificationApi } from '../../rtk-apis/auth/otpVerification';
import { otpSchema } from './helper/Schema';
import '../../styled/pages/login.scss';
import { clearState } from '../../store/slices/Auth/otpVerification';
import { Failed, Loader, Success } from '../../components';
import { forgotPasswordApi } from '../../rtk-apis/auth/forgotPassword';
import { clearState as forgotPasswordClearState } from '../../store/slices/Auth/forgotPassword';
import { servicesManager } from '../../../../platform/app/src/App';

const OtpVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { uiNotificationService } = servicesManager.services;

  const dispatch = useDispatch<AppDispatch>();
  const { status, result, msg, loading } = useSelector(
    (state: RootState) => state.otpVerificationResponse
  );
  const {
    status: forgotPasswordStatus,
    result: forgotPasswordResult,
    msg: forgotPasswordMsg,
    loading: forgotPasswordLoading,
  } = useSelector((state: RootState) => state.forgotPasswordResponse);

  const [timeLeft, setTimeLeft] = useState(119);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isFailedModalOpen, setIsFailedModalOpen] = useState(false);
  const otpInputRefs = useRef([]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timerId = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearInterval(timerId);
    }
  }, [timeLeft]);

  useEffect(() => {
    if (!location.state) {
      navigate('/login');
    }
    setMaskedEmail(maskEmail(location.state.email));
  }, [location.state, navigate]);

  const resendOtp = () => {
    handleResendOtpSubmit({ email: location.state.email });
  };

  const handleResendOtpSubmit = (values: { email: string }) => {
    const payload = {
      email: values.email,
      ...(location.state.hospitalId && { hospitalId: location.state.hospitalId }),
    };
    dispatch(forgotPasswordApi(payload));
  };

  useEffect(() => {
    if (forgotPasswordStatus === 'SUCCESS') {
      setTimeLeft(119);
      setSuccessMessage('OTP has been resent.');
      setIsSuccessModalOpen(true);
    } else if (forgotPasswordStatus === 'ERROR') {
      uiNotificationService.show({
        title: 'Failed to resend OTP',
        message: 'Please try again',
        type: 'error',
        duration: 5000,
        position: 'top-right',
      });
    }
    return () => {
      dispatch(forgotPasswordClearState());
    };
  }, [forgotPasswordStatus]);

  const handleOtpChange = (value, index, setFieldValue, values) => {
    if (value.length > 1) {
      const cleanedValue = value.replace(/[^0-9]/g, '').slice(0, 6);
      const newOtp = cleanedValue.padEnd(6, '').split('');
      setFieldValue('otp', newOtp);
      const lastFilledIndex = Math.min(cleanedValue.length - 1, 5);
      if (otpInputRefs.current[lastFilledIndex]) {
        otpInputRefs.current[lastFilledIndex].focus();
      }
      return;
    }

    if (/^[0-9]?$/.test(value)) {
      const newOtp = [...values.otp];
      newOtp[index] = value;
      setFieldValue('otp', newOtp);

      // Focus next input if it exists
      if (index < 5 && value !== '') {
        otpInputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleOtpKeyDown = (e, index, setFieldValue, values) => {
    if (e.key === 'Backspace') {
      e.preventDefault();

      const newOtp = [...values.otp];
      if (newOtp[index] === '') {
        if (index > 0) {
          otpInputRefs.current[index - 1]?.focus();
        }
      } else {
        newOtp[index] = '';
        setFieldValue('otp', newOtp);
      }
    }
  };

  const handlePaste = (e, setFieldValue, values) => {
    e.preventDefault();
    const pastedData = e.clipboardData
      .getData('text')
      .replace(/[^0-9]/g, '')
      .slice(0, 6);
    if (pastedData.length > 0) {
      const newOtp = pastedData.padEnd(6, '').split('');
      setFieldValue('otp', newOtp);
      const lastFilledIndex = Math.min(pastedData.length - 1, 5);
      if (otpInputRefs.current[lastFilledIndex]) {
        otpInputRefs.current[lastFilledIndex].focus();
      }
    }
  };

  const handleSubmit = values => {
    const enteredOtp = +values.otp.join('');
    const payload = {
      email: location.state.email.toLowerCase(),
      enteredOtp: enteredOtp,
      ...(location.state.hospitalId && { hospitalId: location.state.hospitalId }),
    };

    dispatch(otpVerificationApi(payload));
  };

  useEffect(() => {
    if (status === 'SUCCESS') {
      setIsSuccessModalOpen(true);
      setSuccessMessage('Verification completed successfully.');
      navigate('/resetPassword', {
        state: {
          email: location.state.email,
          hospitalId: location.state.hospitalId,
        },
      });
    }

    if (status === 'ERROR') {
      setIsFailedModalOpen(true);
    }
    return () => {
      dispatch(clearState());
    };
  }, [status, location.state, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <section
        className="login"
        style={customStyles.loginBackground}
      >
        <div className="login__container">
          <Formik
            initialValues={{ otp: new Array(6).fill('') }}
            validationSchema={otpSchema}
            onSubmit={handleSubmit}
          >
            {({ values, handleChange, setFieldValue, errors, dirty }) => (
              <Form>
                <div className="login__wrap">
                  <div className="branding">
                    <div className="branding__logo">
                      <Icon name="two-step-icon" />
                    </div>
                    <div className="branding__info">
                      <h1 className="title title--h2 title--semibold">Two Step Verification</h1>
                      <p className="text">
                        Enter the verification code we sent to <br />
                        {maskedEmail}
                      </p>
                      <h4 className="title title--h5 title--medium">
                        Type your 6 digit security code
                      </h4>
                    </div>
                  </div>

                  <div className="login__form">
                    <div className="form__group">
                      <div className="form__group__otp">
                        <input
                          type="text"
                          style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                          onPaste={e => handlePaste(e, setFieldValue, values)}
                          tabIndex={-1}
                        />
                        {values.otp.map((digit, index) => (
                          <Field
                            key={index}
                            name={`otp[${index}]`}
                            type="text"
                            maxLength={1}
                            className="form__input"
                            value={digit}
                            innerRef={el => (otpInputRefs.current[index] = el)}
                            onChange={e =>
                              handleOtpChange(e.target.value, index, setFieldValue, values)
                            }
                            onKeyDown={e => handleOtpKeyDown(e, index, setFieldValue, values)}
                            onPaste={e => handlePaste(e, setFieldValue, values)}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="form__button textCenter">
                      <button
                        className="btn btn__primary btn--full"
                        type="submit"
                        disabled={!dirty}
                      >
                        Submit
                      </button>

                      <div className="mt-4 flex items-center justify-center pb-0 text-[#49475A]">
                        <Icon
                          name="chevron-left"
                          className="text-gray-600"
                        />
                        <button onClick={handleBack}>Back to Forgot Password</button>
                      </div>

                      <div className="form__optInfo">
                        <p className="para">
                          Didn’t get the code?{' '}
                          <button
                            type="button"
                            disabled={timeLeft > 0}
                            className={`link ${timeLeft > 0 ? 'link--disabled cursor-not-allowed' : 'link'}`}
                            onClick={resendOtp}
                          >
                            Resend Code
                          </button>
                        </p>
                        {timeLeft > 0 && (
                          <p className="para">
                            <Icon name="timer" />
                            <span className="link">
                              {`${Math.floor(timeLeft / 60)}:${String(timeLeft % 60).padStart(2, '0')}`}
                            </span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
        <Loader isLoading={loading} />
        <Success
          isSuccessModalOpen={isSuccessModalOpen}
          handleCloseModal={() => setIsSuccessModalOpen(false)}
          title={successMessage}
        />
        <Failed
          isFailedModalOpen={isFailedModalOpen}
          handleCloseModal={() => setIsFailedModalOpen(false)}
          title="Verification Failed !"
        />
      </section>
    </>
  );
};

export default OtpVerification;
